import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { ThemedLogo } from '@/components/ThemedLogo';
import {
  sanitizeReturnUrl,
  getStoredTournamentReturnUrl,
  clearTournamentReturnContext,
} from '@/lib/authRedirect';
import { z } from 'zod';
import { translateAuthError } from '@/lib/authErrors';

import { SEO } from "@/components/SEO";
const signUpSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'Senha deve ter pelo menos 8 caracteres'),
  confirmPassword: z.string(),
  fullName: z.string().min(2, 'Nome muito curto').max(100, 'Nome muito longo'),
  phone: z.string().min(10, 'WhatsApp inválido').max(20, 'WhatsApp inválido'),
}).refine((d) => d.password === d.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

const signInSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
});

async function resolveRedirectByRole(userId: string, fallback: string): Promise<string> {
  // Check user roles, arenas and onboarding state in parallel
  const [rolesResult, arenasResult, profileResult] = await Promise.all([
    supabase.from('user_roles').select('role').eq('user_id', userId),
    supabase.from('arena_usuarios').select('id').eq('usuario_id', userId).limit(1),
    supabase.from('profiles').select('onboarding_completed').eq('id', userId).maybeSingle(),
  ]);

  const roleList = rolesResult.data?.map(r => r.role) || [];
  const hasArenas = (arenasResult.data?.length || 0) > 0;

  if (roleList.includes('admin')) {
    return '/admin';
  }
  if (hasArenas) {
    return '/minhas-arenas';
  }
  if (roleList.includes('organizador')) {
    return '/painel-organizador';
  }
  // Ainda não escolheu perfil/plano → tela de seleção (não cai direto no portal do atleta)
  if (profileResult.data && profileResult.data.onboarding_completed === false) {
    return fallback;
  }
  return '/atleta';
}


export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showSigninPassword, setShowSigninPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirm, setShowSignupConfirm] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryReturn = sanitizeReturnUrl(searchParams.get('returnUrl') || searchParams.get('redirect'));
  // Fallback: se não veio na query (ex.: usuário voltou pelo link de confirmação
  // de email, que pode descartar a query), tenta o contexto de torneio salvo.
  const explicitReturn = queryReturn || getStoredTournamentReturnUrl();
  // URL para onde o link do email de confirmação deve apontar (preservando o destino final)
  const confirmationRedirect = explicitReturn
    ? `${window.location.origin}/autenticacao?returnUrl=${encodeURIComponent(explicitReturn)}`
    : `${window.location.origin}/autenticacao`;
  const defaultTab = searchParams.get('tab') === 'cadastro' ? 'signup' : 'signin';

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const dest = explicitReturn || await resolveRedirectByRole(session.user.id, '/selecionar-perfil');
        if (explicitReturn) clearTournamentReturnContext();
        navigate(dest, { replace: true });
      } else {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate, explicitReturn]);

  if (checkingAuth) {
    return (
    <>
      <SEO
        title="Entrar"
        description="Acesse sua conta Vello Play para gerenciar torneios, arenas e seu perfil de atleta."
        canonicalPath="/autenticacao"
        noIndex
      />
      <div className="min-h-screen flex items-center justify-center bg-background-gradient">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    </>
    );
  }




  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = (formData.get('email') as string).trim();
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;
    const fullName = (formData.get('fullName') as string).trim();
    const phone = (formData.get('phone') as string).trim();

    const validation = signUpSchema.safeParse({ email, password, confirmPassword, fullName, phone });
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: confirmationRedirect,
        data: {
          full_name: fullName,
          phone: phone,
        },
      },
    });

    if (error) {
      setLoading(false);
      toast.error(translateAuthError(error), { duration: 8000 });
      return;
    }

    // Marca confirmação pendente e envia o link de verificação (não bloqueia o acesso)
    if (data?.user) {
      try {
        await supabase
          .from('profiles')
          .update({ email_confirmation_pending: true })
          .eq('id', data.user.id);
      } catch { /* silencioso: não impede o cadastro */ }

      supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: `${window.location.origin}/?email_confirmed=1`,
        },
      }).catch(() => { /* e-mail de verificação é best-effort */ });
    }

    setLoading(false);
    toast.success('Conta criada! Escolha como você quer usar a Vello Play.');
    const dest = explicitReturn
      ? `/selecionar-perfil?returnUrl=${encodeURIComponent(explicitReturn)}`
      : '/selecionar-perfil';
    navigate(dest);
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = (formData.get('email') as string).trim();
    const password = formData.get('password') as string;

    const validation = signInSchema.safeParse({ email, password });
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      setLoading(false);
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password });

    setLoading(false);

    if (error) {
      toast.error(translateAuthError(error), { duration: 8000 });
    } else {

      const dest = explicitReturn || await resolveRedirectByRole(
        (await supabase.auth.getUser()).data.user!.id,
        '/selecionar-perfil'
      );
      if (explicitReturn) clearTournamentReturnContext();
      navigate(dest);
    }
  };

  const handleForgotPassword = async () => {
    const emailInput = document.getElementById('signin-email') as HTMLInputElement;
    const email = emailInput?.value?.trim();

    if (!email || !email.includes('@')) {
      toast.error('Digite seu email no campo acima para recuperar a senha');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('send-password-reset', {
        body: { email },
      });
      if (error) throw error;
      toast.success(
        'Email de recuperação enviado! Verifique sua caixa de entrada e spam.',
        { duration: 8000 }
      );
    } catch {
      // Don't reveal whether email exists or not for security
      toast.success(
        'Se existe uma conta com este email, você receberá um link de recuperação.',
        { duration: 8000 }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-gradient p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <ThemedLogo className="h-10 w-auto mx-auto mb-4" forceVariant="light" />
          <CardTitle>Bem-vindo ao Vello Play</CardTitle>
          <CardDescription>
            Gestão inteligente de torneios e arenas esportivas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={defaultTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Entrar</TabsTrigger>
              <TabsTrigger value="signup">Criar Conta</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email *</Label>
                  <Input id="signin-email" name="email" type="email" placeholder="seu@email.com" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">Senha *</Label>
                  <div className="relative">
                    <Input
                      id="signin-password"
                      name="password"
                      type={showSigninPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowSigninPassword((v) => !v)}
                      aria-label={showSigninPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                    >
                      {showSigninPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={loading}
                    className="text-xs text-primary hover:underline"
                  >
                    {loading ? 'Enviando...' : 'Esqueci minha senha'}
                  </button>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Entrar
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Nome Completo *</Label>
                  <Input id="signup-name" name="fullName" type="text" placeholder="Seu nome" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email *</Label>
                  <Input id="signup-email" name="email" type="email" placeholder="seu@email.com" required />
                  <p className="text-xs text-muted-foreground">Usado para login e recuperação de senha</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-phone">WhatsApp *</Label>
                  <Input id="signup-phone" name="phone" type="tel" placeholder="(00) 00000-0000" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Senha *</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      name="password"
                      type={showSignupPassword ? 'text' : 'password'}
                      placeholder="Mínimo 8 caracteres"
                      className="pr-10"
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignupPassword((v) => !v)}
                      aria-label={showSignupPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                    >
                      {showSignupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password">Confirmar senha *</Label>
                  <div className="relative">
                    <Input
                      id="signup-confirm-password"
                      name="confirmPassword"
                      type={showSignupConfirm ? 'text' : 'password'}
                      placeholder="Repita a senha"
                      className="pr-10"
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignupConfirm((v) => !v)}
                      aria-label={showSignupConfirm ? 'Ocultar senha' : 'Mostrar senha'}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                    >
                      {showSignupConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Criar Conta
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
